"use client";

import { useEffect, useId, useRef, useState, type ChangeEvent, type FormEvent } from "react";

import { TransitionLink } from "@/components/shell/TransitionLink";
import ui from "@/components/ui/ui.module.css";
import { contactForm as t } from "@/content/contact";
import { HONEYPOT_FIELD, LIMITS, motiveOptions, validateContact, type FieldErrors } from "@/lib/contact";
import styles from "./ContactForm.module.css";

type Status = "idle" | "sending" | "success" | "error";

// Los nombres de los campos son los del formulario anterior (name, email, company,
// service, message, website): el endpoint y data/leads.jsonl no cambian de forma.
const initial = {
  name: "",
  email: "",
  company: "",
  service: motiveOptions[0].id,
  message: "",
  [HONEYPOT_FIELD]: "",
};

type Values = typeof initial;
type Field = keyof Values;

const fieldOrder: (keyof FieldErrors)[] = ["name", "email", "message"];

// Formulario secundario de /contacto (guía 8.11). Valida en el navegador con la misma
// función que el servidor, para mostrar los errores al instante y con el mismo texto;
// el servidor vuelve a validar igual.
export function ContactForm() {
  const id = useId();
  const [values, setValues] = useState<Values>(initial);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [status, setStatus] = useState<Status>("idle");
  const [notice, setNotice] = useState("");
  const successRef = useRef<HTMLHeadingElement>(null);
  const pendingFocus = useRef(false);

  const fid = (f: string) => `${id}-${f}`;

  useEffect(() => {
    if (status === "success") successRef.current?.focus();
  }, [status]);

  // Lleva el foco al primer campo con error, después de pintar el error: así el lector
  // de pantalla lo anuncia junto con el campo y con teclado no hay que buscarlo.
  useEffect(() => {
    if (!pendingFocus.current) return;
    pendingFocus.current = false;
    const first = fieldOrder.find((f) => errors[f]);
    if (first) document.getElementById(`${id}-${first}`)?.focus();
  }, [errors, id]);

  const update = (field: Field) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const value = e.target.value;
    setValues((v) => ({ ...v, [field]: value }));
    if (field in errors) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const showErrors = (errs: FieldErrors) => {
    pendingFocus.current = true;
    setErrors(errs);
  };

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === "sending") return;

    const check = validateContact(values);
    if (check.kind === "invalid") {
      showErrors(check.errors);
      setNotice(t.invalid);
      setStatus("error");
      return;
    }

    setStatus("sending");
    setNotice(t.sending);
    setErrors({});

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const data: { ok?: boolean; error?: string; errors?: FieldErrors } = await res.json().catch(() => ({}));

      if (res.ok && data.ok) {
        setValues(initial);
        setNotice("");
        setStatus("success");
        return;
      }
      if (data.errors) showErrors(data.errors);
      setNotice(data.error ?? (data.errors ? t.invalid : t.generic));
      setStatus("error");
    } catch {
      setNotice(t.network);
      setStatus("error");
    }
  }

  if (status === "success") {
    return (
      // El foco va al título: el lector de pantalla lo anuncia y el teclado sigue desde acá.
      <div className={styles.success}>
        <h3 ref={successRef} tabIndex={-1} className={styles.successTitle}>
          {t.successTitle}
        </h3>
        <p className={styles.successBody}>{t.successBody}</p>
        <button
          type="button"
          className={ui.cta}
          onClick={() => {
            setStatus("idle");
            setNotice("");
          }}
        >
          {t.again}
        </button>
      </div>
    );
  }

  const sending = status === "sending";
  const describe = (f: keyof FieldErrors) => (errors[f] ? fid(`${f}-error`) : undefined);

  return (
    <form
      className={styles.form}
      onSubmit={onSubmit}
      noValidate
      method="post"
      action="/api/contact"
      aria-busy={sending || undefined}
    >
      <noscript>
        <p className={styles.noscript}>{t.noscript}</p>
      </noscript>

      <div className={styles.row}>
        <div className={styles.field}>
          <label htmlFor={fid("name")} className={styles.label}>
            {t.labels.name}
          </label>
          <input
            id={fid("name")}
            name="name"
            type="text"
            autoComplete="name"
            maxLength={LIMITS.name}
            required
            aria-invalid={errors.name ? true : undefined}
            aria-describedby={describe("name")}
            value={values.name}
            onChange={update("name")}
            className={styles.input}
          />
          {errors.name && (
            <p id={fid("name-error")} className={styles.error}>
              {errors.name}
            </p>
          )}
        </div>

        <div className={styles.field}>
          <label htmlFor={fid("email")} className={styles.label}>
            {t.labels.email}
          </label>
          <input
            id={fid("email")}
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            maxLength={LIMITS.email}
            required
            aria-invalid={errors.email ? true : undefined}
            aria-describedby={describe("email")}
            value={values.email}
            onChange={update("email")}
            className={styles.input}
          />
          {errors.email && (
            <p id={fid("email-error")} className={styles.error}>
              {errors.email}
            </p>
          )}
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label htmlFor={fid("company")} className={styles.label}>
            {t.labels.company} <span className={styles.optional}>({t.labels.optional})</span>
          </label>
          <input
            id={fid("company")}
            name="company"
            type="text"
            autoComplete="organization"
            maxLength={LIMITS.company}
            value={values.company}
            onChange={update("company")}
            className={styles.input}
          />
        </div>

        <div className={styles.field}>
          <label htmlFor={fid("service")} className={styles.label}>
            {t.labels.service}
          </label>
          <div className={styles.selectWrap}>
            <select
              id={fid("service")}
              name="service"
              value={values.service}
              onChange={update("service")}
              className={styles.select}
            >
              {motiveOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor={fid("message")} className={styles.label}>
          {t.labels.message}
        </label>
        <textarea
          id={fid("message")}
          name="message"
          rows={6}
          maxLength={LIMITS.message}
          required
          aria-invalid={errors.message ? true : undefined}
          aria-describedby={describe("message")}
          value={values.message}
          onChange={update("message")}
          className={styles.textarea}
        />
        {errors.message && (
          <p id={fid("message-error")} className={styles.error}>
            {errors.message}
          </p>
        )}
      </div>

      {/* Campo trampa: fuera de la vista, del teclado y de los lectores de pantalla. Un
          humano no lo completa; un bot que llena todo, sí. */}
      <div className={styles.honeypot} aria-hidden="true">
        <label htmlFor={fid(HONEYPOT_FIELD)}>{t.labels.honeypot}</label>
        <input
          id={fid(HONEYPOT_FIELD)}
          name={HONEYPOT_FIELD}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={values[HONEYPOT_FIELD]}
          onChange={update(HONEYPOT_FIELD)}
        />
      </div>

      <div className={styles.footer}>
        <button type="submit" className={`${ui.primary} ${styles.submit}`} disabled={sending}>
          {sending ? t.sending : t.submit}
        </button>
        <p className={styles.status} role="status" aria-live="polite" data-tone={status === "error" ? "error" : undefined}>
          {notice}
        </p>
      </div>

      <p className={styles.note}>
        {t.privacyBefore}
        <TransitionLink href="/privacidad" className={styles.inlineLink}>
          {t.privacyLink}
        </TransitionLink>
        {t.privacyAfter}
      </p>
    </form>
  );
}
